import { cohortLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import type { SeatDetail } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

/**
 * What ending a seat takes with it, once resolved. Four states rather than a list: a set
 * that could not be read and a set that is genuinely empty arrive identically, and only
 * one of them means nothing is lost.
 */
export type ConsequenceSource =
  | Readonly<{ kind: 'loading' }>
  | Readonly<{ kind: 'unknown' }>
  | Readonly<{ kind: 'known'; names: readonly string[] }>;

/**
 * A student's seat carries its own cohort memberships, so the seat read answers directly.
 */
export const membershipConsequence = (
  detail: SeatDetail | undefined,
  isLoading: boolean,
  isError = false,
): ConsequenceSource => {
  if (isLoading) return { kind: 'loading' };
  if (isError || detail === undefined || detail.kind === 'unknown') return { kind: 'unknown' };

  return { kind: 'known', names: detail.seat.cohorts.map((cohort) => cohortLabel(cohort)) };
};

/**
 * What a coach revoke ends, read from the one field that IS that set.
 *
 * `authorizedCohortCoaches` lists the attachments keyed to THIS seat — the same column
 * the cascade patches — so it is the detach set by construction, with nothing to keep in
 * sync. It must not be derived any other way: an attachment's authorizing seat is
 * resolved institution-wide, so it can sit on a different roster than its cohort.
 * Deriving by roster understates (a revoke ends coaching the dialog never named) and
 * deriving by coach id overstates.
 *
 * Null means the field did not arrive, which is not an empty set.
 */
export const attachmentConsequence = (
  detail: SeatDetail | undefined,
  isLoading: boolean,
  isError = false,
): ConsequenceSource => {
  if (isLoading) return { kind: 'loading' };
  if (isError || detail === undefined || detail.kind === 'unknown') return { kind: 'unknown' };

  const attached = detail.seat.authorizedCohortCoaches;

  if (attached === null) return { kind: 'unknown' };

  return { kind: 'known', names: attached.map((cohort) => cohortLabel(cohort)) };
};
